import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import {
  Section,
  StickyContainer,
  ContentWrapper,
  ContentContainer,
  GradientText,
  VideoContainer,
  Video,
  ScrollArea,
  FeatureCard,
  FeatureIcon,
  FeatureContent,
  FeatureTitle,
  FeatureDescription,
  FeatureImageContainer,
  FeatureImage,
} from "../styles/sections/KeyFeaturesSectionStyles";

function KeyFeaturesSection() {
  const scrollRef = useRef(null);
  const scroll2Ref = useRef(null);
  const scroll3Ref = useRef(null);
  const scroll4Ref = useRef(null);

  const { scrollYProgress: scroll2Progress } = useScroll({
    target: scroll2Ref,
    offset: ["start end", "end end"],
  });

  const { scrollYProgress: scroll3Progress } = useScroll({
    target: scroll3Ref,
    offset: ["start end", "end end"],
  });

  const { scrollYProgress: scroll4Progress } = useScroll({
    target: scroll4Ref,
    offset: ["start end", "end end"],
  });

  const secondContainerY = useSpring(
    useTransform(scroll2Progress, [0, 1], [600, 0]),
    {
      stiffness: 100,
      damping: 30,
      mass: 1,
    }
  );

  const thirdContainerY = useSpring(
    useTransform(scroll3Progress, [0, 1], [600, 0]),
    {
      stiffness: 100,
      damping: 30,
      mass: 1,
    }
  );

  // 배경색 변경을 위한 상태
  const [bgColors, setBgColors] = React.useState({
    first: window.innerWidth > 768 ? "rgb(247, 96, 0)" : "rgb(0, 0, 0)",
    second: "rgb(0, 0, 0)",
    third: "rgb(0, 0, 0)",
  });

  // 스크롤에 따른 배경색 변경
  React.useEffect(() => {
    if (window.innerWidth <= 768) return;

    const unsubscribe = scroll2Progress.onChange((value) => {
      if (value === 1 && bgColors.third !== "rgb(247, 96, 0)") {
        setBgColors({
          first: "rgb(0, 0, 0)",
          second: "rgb(247, 96, 0)",
          third: "rgb(0, 0, 0)",
        });
      } else if (value === 0 && bgColors.third !== "rgb(247, 96, 0)") {
        setBgColors({
          first: "rgb(247, 96, 0)",
          second: "rgb(0, 0, 0)",
          third: "rgb(0, 0, 0)",
        });
      }
    });

    return () => unsubscribe();
  }, [scroll2Progress, bgColors.third]);

  React.useEffect(() => {
    if (window.innerWidth <= 768) return;

    const unsubscribe = scroll3Progress.onChange((value) => {
      if (value === 1) {
        setBgColors({
          first: "rgb(0, 0, 0)",
          second: "rgb(0, 0, 0)",
          third: "rgb(247, 96, 0)",
        });
      } else if (value === 0) {
        setBgColors({
          first: "rgb(0, 0, 0)",
          second: "rgb(247, 96, 0)",
          third: "rgb(0, 0, 0)",
        });
      }
    });

    return () => unsubscribe();
  }, [scroll3Progress]);

  // Y 값 변화 감지
  React.useEffect(() => {
    if (window.innerWidth <= 768) return;

    const unsubscribe = thirdContainerY.onChange((value) => {
      if (value === 0) {
        setBgColors({
          first: "rgb(0, 0, 0)",
          second: "rgb(0, 0, 0)",
          third: "rgb(247, 96, 0)",
        });
      }
    });

    return () => unsubscribe();
  }, [thirdContainerY]);

  React.useEffect(() => {
    if (window.innerWidth <= 768) return;

    const unsubscribe = secondContainerY.onChange((value) => {
      if (value === 0 && bgColors.third !== "rgb(247, 96, 0)") {
        setBgColors({
          first: "rgb(0, 0, 0)",
          second: "rgb(247, 96, 0)",
          third: "rgb(0, 0, 0)",
        });
      }
    });

    return () => unsubscribe();
  }, [secondContainerY, bgColors.third]);

  return (
    <Section id="keyfeatures" ref={scrollRef}>
      <StickyContainer data-framer-name="Sticky" className="hidden-1lkzo5d">
        <ContentWrapper>
          <ContentContainer bgColor={bgColors.first}>
            <FeatureCard>
              <FeatureIcon>
                <img
                  src="https://framerusercontent.com/images/axhcAytwNgGZ6QZpJvIqx2Hk.png"
                  alt="PvP Mode Icon"
                  width="59"
                  height="49"
                />
              </FeatureIcon>
              <FeatureContent>
                <FeatureTitle>PvP MODE</FeatureTitle>
                <FeatureDescription>
                  Say goodbye to playing alone. Compete against others in PvP
                  Mode.
                </FeatureDescription>
                <FeatureImageContainer>
                  <FeatureImage
                    src="https://framerusercontent.com/images/irSiYXfcTDnkiXd8IUBokZwwbow.webp"
                  />
                </FeatureImageContainer>
              </FeatureContent>
            </FeatureCard>
          </ContentContainer>

          <ContentContainer
            as={motion.div}
            style={{
              y: secondContainerY,
              perspective: 1200,
            }}
            bgColor={bgColors.second}
          >
            <FeatureCard>
              <FeatureIcon>
                <img
                  src="https://framerusercontent.com/images/Pr2z6yrO7ZoTaLC9mGg9ciCFek.png"
                  alt="Abilities Icon"
                  width="59"
                  height="49"
                />
              </FeatureIcon>
              <FeatureContent>
                <FeatureTitle>ABILITIES OF THE GODS</FeatureTitle>
                <FeatureDescription>
                  Discover the unique powers of Greek gods in Titan's Tap.
                </FeatureDescription>
                <FeatureImageContainer>
                  <FeatureImage
                    src="https://framerusercontent.com/images/6O3fUq0hsxdzx8qvMvh2LGfNgco.webp"
                  />
                </FeatureImageContainer>
              </FeatureContent>
            </FeatureCard>
          </ContentContainer>

          <ContentContainer
            as={motion.div}
            style={{
              y: thirdContainerY,
              perspective: 1200,
            }}
            bgColor={bgColors.third}
          >
            <FeatureCard>
              <FeatureIcon>
                <img
                  src="https://framerusercontent.com/images/liT8tsKzFGHaWaZjLEUaDaPmh8.png"
                  alt="Follower System Icon"
                  width="35"
                  height="40"
                />
              </FeatureIcon>
              <FeatureContent>
                <FeatureTitle>FOLLOWER SYSTEM</FeatureTitle>
                <FeatureDescription>
                  Gather your followers and become a great god within Titan's
                  Tap.
                </FeatureDescription>
                <FeatureImageContainer>
                  <FeatureImage
                    src="https://framerusercontent.com/images/gL9pcp5nm5Hhx8OKmJV4GkY3wc.webp"                  />
                </FeatureImageContainer>
              </FeatureContent>
            </FeatureCard>
          </ContentContainer>
        </ContentWrapper>
      </StickyContainer>

      <ScrollArea
 ref={scroll2Ref} data-framer-name="Scroll-2" id="scroll-2" />
      <ScrollArea ref={scroll3Ref} data-framer-name="Scroll-3" id="scroll-3" />
      <ScrollArea ref={scroll4Ref} data-framer-name="Scroll-4" id="scroll-4" />
    </Section>
  );
}

export default KeyFeaturesSection;
